/**
 * PatientsContext - State management for patients
 *
 * Features:
 * - Uses EntityReducer for consistent state updates
 * - Subscribes to EventBus for real-time updates
 * - Handles sync event for initial state
 * - Maintains backward compatibility with existing API
 */

import { createContext, ReactNode, useEffect, useState, useCallback, useRef } from "react";
import { Optional } from "../utils/types.ts";
import { createPatient, deletePatient, editPatient, getPatients } from "../api/patients.ts";
import AddPatientModal from "../components/Patients/AddPatientModal.tsx";
import { useTranslation } from "react-i18next";
import { ORGANIZATION_SWITCHED_EVENT } from "./OrganizationContext.tsx";
import {
  getEventBus,
  EventTypes,
  PatientPayload,
  SyncPayload,
} from "../services/EventBus.ts";
import {
  entityReducer,
  EntityAction,
} from "../services/EntityReducer.ts";

export type Gender = 'male' | 'female';

export type WebsocketPatientPayload = Patient;

export type Patient = {
  id: number;
  first_name: string;
  last_name: string;
  dob: string;
  gender: Gender | null;
  user_id: number;
  external_emr_id?: string | null;
}

export type PatientFormData = {
  firstName: string;
  lastName: string;
  dob?: string;
  gender?: Gender | null;
}

export type PatientContextType = {
  patients: Optional<Patient[]>;
  setPatients: (user: Optional<Patient[]>) => void;
  loading: boolean;
  activePatient: Optional<Patient>;
  setActivePatient: (user: Optional<number>) => void;
  fetchPatients: (patientId?: number) => void;
  openAddPatientModal: () => void;
  handleAddPatient: (patientFormData: PatientFormData) => Promise<Patient>;
  handleEditPatient: (patientFormData: PatientFormData) => Promise<Patient>;
  handleDeletePatient: (patientId: number) => Promise<void>;
  assignPatientModalOpen: boolean;
  setAssignPatientModalOpen: (open: boolean) => void;
  addNewPatient: (newData: WebsocketPatientPayload) => void;
  removePatient: (payload: WebsocketPatientPayload) => void;
}


const contextDefaultValues: PatientContextType = {
  patients: null,
  setPatients: () => {},
  loading: false,
  activePatient: null,
  setActivePatient: () => {},
  fetchPatients: async () => {},
  openAddPatientModal: () => {},
  handleAddPatient: () => Promise.resolve({} as Patient),
  handleEditPatient: () => Promise.resolve({} as Patient),
  handleDeletePatient: () => Promise.resolve(),
  assignPatientModalOpen: false,
  setAssignPatientModalOpen: () => {},
  addNewPatient: () => {},
  removePatient: () => {}
}

const PatientsContext = createContext<PatientContextType>(contextDefaultValues);

/**
 * Normalize a patient payload from API or WebSocket to Patient type
 */
function normalizePatient(payload: PatientPayload): Patient {
  return {
    id: payload.id,
    first_name: payload.first_name ?? '',
    last_name: payload.last_name ?? '',
    dob: payload.dob ?? '',
    gender: payload.gender ? (payload.gender as Gender) : null,
    user_id: 0, // API doesn't always return this
    external_emr_id: payload.external_emr_id ?? null,
  };
}

/**
 * Sort function for patients (alphabetically by name)
 */
const sortPatients = (a: Patient, b: Patient) => {
  const firstNameComparison = a.first_name.localeCompare(b.first_name);
  if (firstNameComparison !== 0) return firstNameComparison;
  return a.last_name.localeCompare(b.last_name);
};

function PatientsProvider({ children }: { children: ReactNode }) {
  const [patients, setPatients] = useState<Optional<Patient[]>>(null);
  const [activePatient, setActivePatient] = useState<Optional<Patient>>(null);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assignPatientModalOpen, setAssignPatientModalOpen] = useState(false);
  const { t } = useTranslation();
  const eventBus = getEventBus();

  // Track if initial data was loaded via sync
  const initialLoadDone = useRef(false);

  /**
   * Apply entity action to patients state
   */
  const dispatchPatientAction = useCallback((action: EntityAction<Patient>) => {
    setPatients(prev => {
      if (prev === null) {
        if (action.type === 'SET_ALL') {
          return action.entities.sort(sortPatients);
        }
        // Initialize with the single entity if we get an UPSERT before SET_ALL
        if (action.type === 'UPSERT') {
          return [action.entity];
        }
        // For REMOVE on null state, nothing to do
        if (action.type === 'REMOVE') {
          return null;
        }
        throw new Error(`Unexpected action type "${action.type}" when patients is null`);
      }
      return entityReducer(prev, action, sortPatients);
    });
  }, []);

  // Subscribe to EventBus for real-time updates
  useEffect(() => {
    const subscriptions = [
      // Full state sync
      eventBus.subscribe(EventTypes.SYNC, (payload: SyncPayload) => {
        const normalizedPatients = payload.patients
          .map(p => normalizePatient(p as unknown as PatientPayload))
          .filter(p => !!p.first_name && !!p.last_name);
        dispatchPatientAction({ type: 'SET_ALL', entities: normalizedPatients });
        initialLoadDone.current = true;
      }),

      // Patient created
      eventBus.subscribe(EventTypes.PATIENT_CREATED, (payload: PatientPayload) => {
        const patient = normalizePatient(payload);
        if (patient.first_name && patient.last_name) {
          dispatchPatientAction({ type: 'UPSERT', entity: patient });
        }
      }),

      // Patient updated
      eventBus.subscribe(EventTypes.PATIENT_UPDATED, (payload: PatientPayload) => {
        const patient = normalizePatient(payload);
        dispatchPatientAction({ type: 'UPSERT', entity: patient });

        // Update active patient if it matches
        setActivePatient(prev => {
          if (prev && prev.id === patient.id) {
            return patient;
          }
          return prev;
        });
      }),

      // Patient deleted
      eventBus.subscribe(EventTypes.PATIENT_DELETED, (payload) => {
        dispatchPatientAction({ type: 'REMOVE', id: payload.id });

        // Clear active patient if it was deleted
        setActivePatient(prev => {
          if (prev && prev.id === payload.id) {
            return null;
          }
          return prev;
        });

        // Redirect if viewing the deleted patient
        if (window.location.pathname.includes(`/patients/${payload.id}`)) {
          window.location.href = '/patients';
        }
      }),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [eventBus, dispatchPatientAction]);

  // Listen for organization switch events and clear data
  useEffect(() => {
    function handleOrganizationSwitch() {
      setPatients(null);
      setActivePatient(null);
      initialLoadDone.current = false;
    }

    window.addEventListener(ORGANIZATION_SWITCHED_EVENT, handleOrganizationSwitch);
    return () => {
      window.removeEventListener(ORGANIZATION_SWITCHED_EVENT, handleOrganizationSwitch);
    };
  }, []);

  async function fetchPatients() {
    // Skip if already loaded via sync
    if (patients || initialLoadDone.current) return patients;

    setLoading(true);
    const response = await getPatients();
    const filtered = response.filter(p => !!p.first_name && !!p.last_name);
    setPatients(filtered.sort(sortPatients));
    setLoading(false);
    return response;
  }

  async function handleSetActivePatient(patientId: Optional<number>) {
    if (!patientId) {
      setActivePatient(null);
      return;
    }
    const response = await fetchPatients();
    if (!response) throw new Error(t('errors.noPatients'));
    setActivePatient(response.find(s => s.id === patientId) ?? null);
  }

  function openAddPatientModal() {
    setAddPatientModalOpen(true);
  }

  async function handleAddPatient(patientFormData: PatientFormData) {
    const response = await createPatient(patientFormData);
    // Always add to local state - patient should be available immediately
    // regardless of which page the user is on
    dispatchPatientAction({ type: 'UPSERT', entity: response });
    return response;
  }

  async function handleEditPatient(patientFormData: PatientFormData) {
    if (!activePatient || !patients) throw new Error(t('errors.noActivePatients'));
    const response = await editPatient(patientFormData, activePatient!.id);
    dispatchPatientAction({ type: 'UPSERT', entity: response });
    setActivePatient(response);
    return response;
  }

  async function handleDeletePatient(patientId: number) {
    if (!patients) throw new Error(t('errors.noPatients'));
    const index = patients.findIndex(p => p.id === patientId);
    if (index === -1) throw new Error(t('errors.patientNotFound'));
    await deletePatient(patientId);
    dispatchPatientAction({ type: 'REMOVE', id: patientId });
    if (activePatient && activePatient.id === patientId) {
      setActivePatient(null);
    }
  }

  // Legacy function - now handled via EventBus
  function addNewPatient(newData: WebsocketPatientPayload) {
    if (newData.first_name && newData.last_name) {
      dispatchPatientAction({ type: 'UPSERT', entity: newData });
    }
  }

  // Legacy function - now handled via EventBus
  function removePatient(payload: WebsocketPatientPayload) {
    dispatchPatientAction({ type: 'REMOVE', id: payload.id });
    if (window.location.pathname.includes(`/patients/${payload.id}`)) {
      window.location.href = '/patients';
    }
  }

  return (
    <PatientsContext.Provider value={{
      patients,
      setPatients,
      activePatient,
      setActivePatient: handleSetActivePatient,
      fetchPatients,
      openAddPatientModal,
      handleAddPatient,
      handleEditPatient,
      loading,
      handleDeletePatient,
      assignPatientModalOpen,
      setAssignPatientModalOpen,
      addNewPatient,
      removePatient
    }}>
      {children}
      <AddPatientModal open={addPatientModalOpen} setOpen={setAddPatientModalOpen}/>
    </PatientsContext.Provider>
  )
}

export { PatientsContext, PatientsProvider };
